import { useState, FormEvent } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const params = new URLSearchParams();
      params.append("email", email);
      const res = await fetch("https://readdy.ai/api/form/d72fp92aegmdqoca7tfg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const socials = [
    { icon: "ri-instagram-line", label: "Instagram", href: "#" },
    { icon: "ri-twitter-x-line", label: "Twitter/X", href: "#" },
    { icon: "ri-youtube-line", label: "YouTube", href: "#" },
    { icon: "ri-linkedin-box-line", label: "LinkedIn", href: "#" },
  ];

  const links = [
    { label: "서비스 소개", href: "/#solutions" },
    { label: "아르트레너 진단", href: "/#canvas" },
    { label: "플랜 및 가격", href: "/#pricing" },
    { label: "프로세스", href: "/#process" },
    { label: "블로그", href: "/blog" },
  ];

  return (
    <footer style={{ background: "linear-gradient(180deg, #06080F 0%, #0A0512 100%)" }}>
      {/* Newsletter CTA Block */}
      <div className="relative border-t border-white/5 py-20 lg:py-28 px-6 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(168,85,247,0.08) 0%, transparent 70%)" }}
        />
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-7 flex items-center justify-center" style={{ background: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.3)" }}>
            <i className="ri-mail-send-line text-brand-purple text-2xl" />
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-4">
            크리에이터 비즈니스 인사이트 받기
          </h3>
          <p className="text-white/50 font-body text-sm mb-10 leading-loose">
            아티스트가 비즈니스를 만들어가는 이야기, 아르트레너의 노하우를 이메일로 받아보세요.
          </p>

          {status === "success" ? (
            <div className="flex items-center justify-center gap-3 px-6 py-4 rounded-2xl" style={{ background: "rgba(20,241,217,0.08)", border: "1px solid rgba(20,241,217,0.3)" }}>
              <i className="ri-check-double-line text-brand-teal text-xl" />
              <p className="text-brand-teal font-display font-medium">구독 신청이 완료됐어요! 곧 첫 인사이트를 보내드릴게요.</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="newsletter-subscription"
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="이메일 주소를 입력하세요"
                required
                className="flex-1 px-5 py-3 rounded-2xl text-sm font-body text-white placeholder-white/30 outline-none transition-all duration-300 border focus:border-brand-purple/60"
                style={{ background: "rgba(255,255,255,0.05)", borderColor: "rgba(255,255,255,0.1)" }}
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="px-6 py-3 rounded-2xl text-sm font-display font-semibold text-white transition-all duration-300 cursor-pointer whitespace-nowrap flex-shrink-0 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)", boxShadow: "0 0 16px rgba(168,85,247,0.3)" }}
              >
                {status === "loading" ? (
                  <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin" /> 신청 중...</span>
                ) : (
                  "구독 신청"
                )}
              </button>
            </form>
          )}
          {status === "error" && (
            <p className="mt-3 text-sm text-red-400 font-body">
              오류가 발생했어요. 잠시 후 다시 시도해주세요.
            </p>
          )}
        </div>
      </div>

      {/* Main Footer */}
      <div className="border-t border-white/5 py-14 lg:py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start justify-between gap-12 mb-12">
            {/* Brand */}
            <div className="flex flex-col gap-5 max-w-xs">
              <div className="flex items-center gap-3">
                <img
                  src="https://public.readdy.ai/ai/img_res/b51260fb-bf77-436f-865d-1c38bc3c656b.png"
                  alt="ARTREPRENEUR Logo"
                  className="w-8 h-8 object-contain"
                />
                <span className="font-display font-bold text-base tracking-widest text-white">ARTREPRENEUR</span>
              </div>
              <p className="text-white/40 text-sm font-body leading-loose">
                예술가를 위한 프리미엄 컴퍼니빌더. 당신의 창작 IP를 지속 가능한 비즈니스로 전환합니다.
              </p>
              {/* Socials */}
              <div className="flex items-center gap-3">
                {socials.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={s.label}
                    className="w-9 h-9 flex items-center justify-center rounded-xl text-white/40 hover:text-white transition-all duration-200 cursor-pointer"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                  >
                    <i className={`${s.icon} text-base`} />
                  </a>
                ))}
              </div>
            </div>

            {/* Nav Links */}
            <div className="flex flex-wrap gap-16">
              <div>
                <p className="text-xs font-display font-semibold tracking-widest uppercase text-white/40 mb-5">서비스</p>
                <ul className="flex flex-col gap-4">
                  {links.map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="text-sm text-white/50 hover:text-white transition-colors cursor-pointer font-body whitespace-nowrap">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-display font-semibold tracking-widest uppercase text-white/40 mb-5">회사</p>
                <ul className="flex flex-col gap-4">
                  {[
                    { label: "아르트레너 소개", href: "/about" },
                    { label: "빌더팀 채용", href: "/careers" },
                    { label: "파트너십 문의", href: "/partnership" },

                  ].map((link) => (
                    <li key={link.label}>
                      <a href={link.href} className="text-sm text-white/50 hover:text-white transition-colors cursor-pointer font-body whitespace-nowrap">
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/30 font-body">
              © 2026 ARTREPRENEUR. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              {[
                { label: "이용약관", href: "/terms" },
                { label: "개인정보처리방침", href: "/privacy" },
                { label: "쿠키 정책", href: "/cookies" },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-xs text-white/30 hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
